import {updateHistory} from 'app/actions/historyActions';
import {catchDataError} from 'app/helpers/apiHelpers';
import {Dispatch} from 'react';
import {
  Action,
  initialPlayerStatsState,
  PlayerStatsState,
} from 'app/components/team/playerstats/TeamPlayerStatsReducer';
import {api} from 'app/services/api';
import {AxiosResponse} from 'axios';
import {
  ErrorData,
  Player,
  PlayerStatsAlltimeActionTypes,
  PlayerStatsAlltimeResponse,
  PlayerStatsSeasonActionTypes,
} from 'app/components/team/playerstats/types';
import {fetchNextApiCall} from 'app/services/fetchNextApiCall';
import {GridSortModel} from '@mui/x-data-grid-pro';

const getTeamPlayerStats = (teamSlug: string) => {
  return api.get(`/teams/${teamSlug}/player-stats`);
};

function updatePlayerStats(data: Player[]): Action {
  return {
    type: PlayerStatsSeasonActionTypes.UPDATE,
    data,
  };
}

function errorPlayerStats(errorData: ErrorData): Action {
  return {
    type: PlayerStatsSeasonActionTypes.ERROR,
    errorData,
  };
}

function fetchingPlayerStats(): Action {
  return {
    type: PlayerStatsSeasonActionTypes.FETCHING,
  };
}

export const fetchTeamPlayerStatsSSR = (teamSlug: string) => {
  return async function (dispatch: Dispatch<any>) {
    try {
      const response = await getTeamPlayerStats(teamSlug);
      dispatch(
        updateHistory('undefined', {
          store: 'TeamPlayerStatsPage',
          data: {
            ...initialPlayerStatsState,
            season: {
              ...initialPlayerStatsState.season,
              players: response.data,
            },
          },
        })
      );
    } catch (error) {
      const errorData = catchDataError(error);
      dispatch(
        updateHistory('undefined', {
          store: 'TeamPlayerStatsPage',
          data: {...initialPlayerStatsState, season: {...initialPlayerStatsState.season, error: errorData}},
        })
      );
    }
  };
};

export const fetchTeamPlayerStats = (teamSlug: string) => {
  return function (dispatch: Dispatch<Action>, state: PlayerStatsState) {
    if (state.season.players.length) {
      return;
    }

    dispatch(fetchingPlayerStats());
    return getTeamPlayerStats(teamSlug)
      .then(response => {
        dispatch(updatePlayerStats(response.data));
      })
      .catch(error => {
        const errorData = catchDataError(error);
        dispatch(errorPlayerStats(errorData));
      });
  };
};

const getPlayerStatsAlltime = (teamSlug: string, sortQuery?: string): Promise<AxiosResponse> => {
  return api.get(`/teams/${teamSlug}/alltime-player-stats?limit=15${sortQuery ? `&sort=${sortQuery}` : ''}`);
};

function updatePlayerStatsAlltime(data: PlayerStatsAlltimeResponse): Action {
  return {
    type: PlayerStatsAlltimeActionTypes.UPDATE,
    data,
  };
}

function addPlayerStatsAlltime(data: PlayerStatsAlltimeResponse): Action {
  return {
    type: PlayerStatsAlltimeActionTypes.ADD,
    data,
  };
}

function errorPlayerStatsAlltime(errorData: ErrorData): Action {
  return {
    type: PlayerStatsAlltimeActionTypes.ERROR,
    errorData,
  };
}

function fetchingPlayerStatsAlltime(): Action {
  return {
    type: PlayerStatsAlltimeActionTypes.FETCHING,
  };
}

export function updatePlayerStatsAlltimeSortmodel(data: GridSortModel): Action {
  return {
    type: PlayerStatsAlltimeActionTypes.SORTMODEL,
    data,
  };
}

export function fetchPlayerStatsAlltimeSSR(teamSlug: string) {
  return async function (dispatch: Dispatch<any>) {
    try {
      const response: AxiosResponse<PlayerStatsAlltimeResponse> = await getPlayerStatsAlltime(teamSlug);
      dispatch(
        updateHistory('undefined', {
          store: 'TeamPlayerStatsPage',
          data: {
            ...initialPlayerStatsState,
            alltime: {
              ...initialPlayerStatsState.alltime,
              players: response.data,
            },
          },
        })
      );
    } catch (error) {
      const errorData = catchDataError(error);
      dispatch(
        updateHistory('undefined', {
          store: 'TeamPlayerStatsPage',
          data: {
            ...initialPlayerStatsState,
            alltime: {
              ...initialPlayerStatsState.alltime,
              error: errorData,
            },
          },
        })
      );
    }
  };
}

export function fetchPlayerStatsAlltime(teamSlug: string, reload?: boolean, sortQuery?: string) {
  return async function (dispatch: Dispatch<Action>, state: PlayerStatsState) {
    if (state.alltime.players && !reload) {
      return;
    }

    dispatch(fetchingPlayerStatsAlltime());
    try {
      const response: AxiosResponse<PlayerStatsAlltimeResponse> = await getPlayerStatsAlltime(teamSlug, sortQuery);
      dispatch(updatePlayerStatsAlltime(response.data));
    } catch (error) {
      const errorData: ErrorData = catchDataError(error);
      dispatch(errorPlayerStatsAlltime(errorData));
    }
  };
}

export function fetchNextPlayers(url: string, add: boolean) {
  return async function (dispatch: Dispatch<Action>) {
    dispatch(fetchingPlayerStatsAlltime());
    try {
      const response = await fetchNextApiCall<PlayerStatsAlltimeResponse>(api, url);
      if (add) {
        dispatch(addPlayerStatsAlltime(response.data));
      } else {
        dispatch(updatePlayerStatsAlltime(response.data));
      }
    } catch (error) {
      const errorData: ErrorData = catchDataError(error);
      dispatch(errorPlayerStatsAlltime(errorData));
    }
  };
}
